import env from 'config';
import { isBefore, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { ITrainedStatusConfig } from 'interfaces/i-training';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { meanObject } from 'lib_ts/classes/math.utilities';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATETIME_FORMAT_SHORT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { ANONYMOUS_HITTER_ID } from 'lib_ts/enums/hitters.enums';
import { PitchListOwner, TrainedStatus } from 'lib_ts/enums/pitch-list.enums';
import {
  NON_SAMPLE_LIST_TYPES,
  PitcherHand,
  ReferenceListType,
} from 'lib_ts/enums/pitches.enums';
import { EMPTY_SELECT_VALUE, RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { Competition } from 'lib_ts/enums/team.enums';
import {
  MIN_TRAINING_SHOTS,
  PresetTrainingMode,
} from 'lib_ts/enums/training.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { ISessionCurrent } from 'lib_ts/interfaces/common/i-login';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { ICustomIO } from 'lib_ts/interfaces/csv/i-custom';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { IVideo } from 'lib_ts/interfaces/i-video';
import {
  IPitch,
  IPitchList,
  IReassignOptions,
} from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';

interface IBaseConfig {
  options: IReassignOptions;
  role: UserRole;
  teamID: string;
  parentDef: string;
}

interface ICopyConfig extends IBaseConfig {}

interface IReassignConfig extends ICopyConfig {
  parentId: string;
}

const getBaseOptions = (config: IBaseConfig) => {
  switch (config.parentDef) {
    case PitchListOwner.User: {
      return config.options.users.map((m) => {
        const o: IOption = {
          label: m.email,
          group: m.machineID,
          value: m._id,
        };

        return o;
      });
    }

    case PitchListOwner.Machine: {
      return config.options.machines.map((m) => {
        const o: IOption = {
          label: `${m.machineID}${m.sandbox ? ' (Sandbox)' : ''}`,
          group: config.options.teams.find((t) => t._id === m._parent_id)?.name,
          value: m._id,
        };

        return o;
      });
    }

    case PitchListOwner.Team: {
      return config.options.teams.map((m) => {
        const o: IOption = {
          label: m.name,
          group: m.league ?? Competition.None,
          value: m._id,
        };

        return o;
      });
    }

    default: {
      return [
        {
          label: 'common.select-a-parent-type-first',
          value: EMPTY_SELECT_VALUE,
          disabled: true,
        },
      ];
    }
  }
};

export const getPitchYearOptions = (): IOption[] => {
  const first = 2020;
  const current = new Date().getFullYear();

  return Array.from({ length: 2 + current - first }, (_, i) => i + first).map(
    (yr) => ({ label: yr.toString(), value: yr.toString() })
  );
};

export const getCopyToIdOptions = (config: ICopyConfig): IOption[] => {
  return getBaseOptions(config).sort((a, b) =>
    StringHelper.safeSort(a.label, b.label)
  );
};

export const getReassignToIdOptions = (config: IReassignConfig): IOption[] => {
  return getBaseOptions(config)
    .filter((o) => {
      /** don't allow reassignment to the current parent */
      return o.value !== config.parentId;
    })
    .sort((a, b) => StringHelper.safeSort(a.label, b.label));
};

const MAX_SPEED_DELTA_MPH = 2;
const MAX_SPIN_DELTA_RPM = 500;

interface IPitchError {
  text: string;
  color: RadixColor;
}

export const CREATE_NEW_LIST_ID = 'create-new-list';

export class PitchListHelper {
  static pitcherHand = (value?: PitcherHand) => {
    switch (value) {
      case PitcherHand.LHP: {
        return t('common.abbrev-left-handed-pitcher');
      }

      case PitcherHand.RHP: {
        return t('common.abbrev-right-handed-pitcher');
      }

      default: {
        return '???';
      }
    }
  };

  /** converts a pitch to the Custom CSV format */
  static convertToCustomExport(config: {
    pitch: IPitch;
    videosDict: DictOf<IVideo>;
    plate_ft: number;
  }): ICustomIO {
    const { pitch } = config;

    const roundForCSV = (n: number) => Math.round(n * 100) / 100;

    const seams = BallHelper.getPitchSeams(pitch);

    const pitchYear =
      pitch.year?.trim() ?? pitch.mlb_repertoire?.season?.toString() ?? '';

    const output: ICustomIO = {
      PitchID: pitch._id,

      PitchTitle: pitch.name ?? 'Unnamed',
      PitchType: pitch.type ?? 'No Type',
      PitcherFullName: pitch.pitcher?.trim(),
      PitcherTeam: pitch.team?.trim(),

      PitchYear: pitchYear,

      WindupVideoID: pitch.windupVideoID ?? '--',
      VideoTitleWindup:
        config.videosDict[pitch.windupVideoID ?? 'none']?.VideoTitle ?? '--', // non-empty fallback ensures the column gets created

      StretchVideoID: pitch.stretchVideoID ?? '--',
      VideoTitleStretch:
        config.videosDict[pitch.stretchVideoID ?? 'none']?.VideoTitle ?? '--', // non-empty fallback ensures the column gets created

      PlateLocHeight:
        pitch.plate_loc_backup !== undefined
          ? roundForCSV(pitch.plate_loc_backup.plate_z)
          : undefined,

      PlateLocSide:
        pitch.plate_loc_backup !== undefined
          ? roundForCSV(pitch.plate_loc_backup.plate_x)
          : undefined,

      ReleaseX: roundForCSV(pitch.bs.px),
      ReleaseY: roundForCSV(config.plate_ft),
      ReleaseZ: roundForCSV(pitch.bs.pz),
      ReleaseV: roundForCSV(-pitch.bs.vy),
      SpinX: roundForCSV(pitch.bs.wx),
      SpinY: roundForCSV(pitch.bs.wy),
      SpinZ: roundForCSV(pitch.bs.wz),
      SeamLat: roundForCSV(seams.latitude_deg),
      SeamLon: roundForCSV(seams.longitude_deg),

      HorizontalBreakIn: pitch.breaks
        ? // put it back into hawkeye frame of ref (from trajekt)
          -pitch.breaks.xInches
        : undefined,

      VerticalBreakIn: pitch.breaks?.zInches,
    };

    return output;
  }

  static getCopyToIdOptions = (config: ICopyConfig): IOption[] => {
    return getBaseOptions(config).sort((a, b) =>
      StringHelper.safeSort(a.label, b.label)
    );
  };

  static getReassignToIdOptions = (config: IReassignConfig): IOption[] => {
    return getBaseOptions(config)
      .filter((o) => {
        /** don't allow reassignment to the current parent */
        return o.value !== config.parentId;
      })
      .sort((a, b) => StringHelper.safeSort(a.label, b.label));
  };

  static getAvgShot = (shots: IMachineShot[]): Partial<IMachineShot> => {
    if (shots.length === 0) {
      return {};
    }

    if (shots.length === 0) {
      return {};
    }

    const bss = shots.filter((s) => s.bs).map((s) => s.bs);
    const avgBS = bss.length > 0 ? meanObject(bss) : undefined;

    const targetMss = shots.filter((s) => s.target_ms).map((s) => s.target_ms);
    const avgTargetMS =
      targetMss.length > 0 ? meanObject(targetMss) : undefined;

    const trajs = shots.filter((s) => s.traj).map((s) => s.traj);
    const avgTraj = trajs.length > 0 ? meanObject(trajs) : undefined;

    const actualMss = shots.filter((s) => s.actual_ms).map((s) => s.actual_ms);
    const avgActualMS =
      actualMss.length > 0 ? meanObject(actualMss) : undefined;

    const breaks = shots.filter((s) => s.break).map((s) => s.break);
    const avgBreak = breaks.length > 0 ? meanObject(breaks) : undefined;

    const output: Partial<IMachineShot> = {
      ...shots[0],
      bs: avgBS,
      target_ms: avgTargetMS,
      traj: avgTraj,
      actual_ms: avgActualMS,
      break: avgBreak,
    };

    return output as Partial<IMachineShot>;
  };

  /**
   * comparison between pitch's bs and the actual bs (avg) from matching shots
   * @param pitch
   * @param machine
   * @returns undefined if there's nothing wrong with the pitch, otherwise provides a summary of the error
   */
  static getError = (
    pitch: IPitch,
    shots: IMachineShot[]
  ): IPitchError | undefined => {
    if (shots.length === 0) {
      return undefined;
    }

    const averagePitch = this.getAvgShot(shots);
    if (!averagePitch?.bs) {
      return undefined;
    }

    if (!averagePitch?.traj) {
      return undefined;
    }

    const actualSpeed = TrajHelper.getSpeedFromTraj(averagePitch.traj);
    const targetSpeed = TrajHelper.getSpeedFromTraj(pitch.traj);

    if (Math.abs(actualSpeed - targetSpeed) > MAX_SPEED_DELTA_MPH) {
      const error: IPitchError = {
        color: RADIX.COLOR.WARNING,
        text: [
          `Actual speed (${actualSpeed.toFixed(
            1
          )} mph) differs from target speed (${targetSpeed.toFixed(
            1
          )} mph) by more than ${MAX_SPEED_DELTA_MPH} mph.`,
          'Please reset training data at your earliest convenience.',
        ].join(' '),
      };

      return error;
    }

    const axes: { name: string; actual: number; target: number }[] = [
      { name: 'back', actual: averagePitch.bs.wx, target: pitch.bs.wx },
      { name: 'gyro', actual: averagePitch.bs.wy, target: pitch.bs.wy },
      { name: 'side', actual: averagePitch.bs.wz, target: pitch.bs.wz },
    ];

    const errorSpinAxis = axes.find(
      (m) => Math.abs(m.actual - m.target) > MAX_SPIN_DELTA_RPM
    );

    if (errorSpinAxis) {
      const error: IPitchError = {
        text: [
          `Actual ${errorSpinAxis.name} spin (${errorSpinAxis.actual.toFixed(
            0
          )} rpm) differs from target ${
            errorSpinAxis.name
          } spin (${errorSpinAxis.target.toFixed(
            0
          )} rpm) by more than ${MAX_SPIN_DELTA_RPM} rpm.`,
          'Please reset training data at your earliest convenience.',
        ].join(' '),
        color: RADIX.COLOR.WARNING,
      };

      return error;
    }
  };

  static enumeratePitches = (pitches: IPitch[], max: number): string => {
    const lines: string[] = pitches
      .filter((_, i) => i < max)
      .map((p) => ` - ${p.name}`);
    const diffMax = pitches.length - max;
    if (diffMax > 0) {
      lines.push(` - ...and ${diffMax} more`);
    }
    return lines.join('\n');
  };

  static getListOptions = (
    lists: IPitchList[],
    auth: ISessionCurrent,
    skipCreateNew?: boolean
  ): IOption[] => {
    interface IListsDict {
      [key: string]: IPitchList[];
    }

    const parentDict: IListsDict = {};

    const basicLists = lists.filter((l) => !l.reference_type);

    parentDict['constants.pitch-list-personal'] = basicLists.filter(
      (l) => l._parent_def === PitchListOwner.User
    );

    if (auth?.machine_lists) {
      parentDict[
        t('constants.pitch-list-x-lists', {
          x: auth.machineID,
        }).toString()
      ] = basicLists.filter((l) => l._parent_def === PitchListOwner.Machine);
    }

    if (auth?.team_lists) {
      parentDict['constants.pitch-list-team'] = basicLists.filter(
        (l) => l._parent_def === PitchListOwner.Team
      );
    }

    const extendedLists = lists.filter((l) => !!l.reference_type);

    if (env.enable.sample_lists && auth.role === 'admin') {
      parentDict['constants.pitch-list-samples'] = extendedLists.filter(
        (l) => l.reference_type === ReferenceListType.Sample
      );
    }

    if (auth.role === 'admin') {
      parentDict['constants.pitch-list-references'] = extendedLists.filter(
        (l) =>
          l.reference_type && NON_SAMPLE_LIST_TYPES.includes(l.reference_type)
      );
    }

    const options: IOption[] = [
      {
        invisible: skipCreateNew,
        label: t('common.add-a-new-list'),
        value: CREATE_NEW_LIST_ID,
      },
    ];

    Object.keys(parentDict).forEach((parent) => {
      const lists = parentDict[parent];
      if (lists.length === 0) {
        return;
      }

      options.push(
        ...lists.map((l) => {
          const o: IOption = {
            label: l.name,
            value: l._id,
            group: `${t(parent)}${l.folder ? `: ${l.folder}` : ''}`,
          };

          return o;
        })
      );
    });

    return options;
  };

  static getTrainedStatus(config: ITrainedStatusConfig): TrainedStatus {
    const { tms } = config;

    if (!tms) {
      return TrainedStatus.Untrained;
    }

    if (tms.deleted) {
      return TrainedStatus.Untrained;
    }

    if (tms.debug) {
      return TrainedStatus.Debug;
    }

    if (
      config.last_hardware_changed &&
      isBefore(parseISO(tms._changed), parseISO(config.last_hardware_changed))
    ) {
      return TrainedStatus.Untrained;
    }

    // prevent deleted training data from being used
    const usableShotIDs = (tms.shotIDs ?? []).filter(
      (id) => !tms.discardedShotIDs?.includes(id)
    );

    if (usableShotIDs.length < MIN_TRAINING_SHOTS) {
      return TrainedStatus.Untrained;
    }

    switch (tms.preset?.mode) {
      case PresetTrainingMode.Quick: {
        return TrainedStatus.Trained;
      }

      case PresetTrainingMode.Custom:
      case PresetTrainingMode.Precision: {
        return TrainedStatus.Precision;
      }

      // should only happen if a pitch was manually trained
      default: {
        return TrainedStatus.Trained;
      }
    }
  }

  static getTrainedStatusSort = (v: TrainedStatus): number => {
    switch (v) {
      case TrainedStatus.Precision: {
        return 2;
      }
      case TrainedStatus.Trained: {
        return 1;
      }
      case TrainedStatus.Untrained:
      default: {
        return 0;
      }
    }
  };

  static getTrainedStatusTooltip(config: ITrainedStatusConfig) {
    const lines: string[] = [
      `${t('pl.status')}: ${PitchListHelper.getTrainedStatus(config)}`,
    ];

    if (config.tms) {
      if (config.tms.ms.last_built) {
        lines.push(
          `${t('common.updated')}: ${format(
            parseISO(config.tms.ms.last_built),
            LOCAL_DATETIME_FORMAT_SHORT,
            { timeZone: LOCAL_TIMEZONE }
          )}`
        );
      }

      if (config.tms._changed) {
        lines.push(
          `${t('pl.trained')}: ${format(
            parseISO(config.tms._changed),
            LOCAL_DATETIME_FORMAT_SHORT,
            { timeZone: LOCAL_TIMEZONE }
          )}`
        );
      }
    }

    if (config.modelName) {
      lines.push(`${t('common.model')}: ${config.modelName}`);
    }

    return lines.join('\n\n');
  }

  // select input needs an empty string (instead of undefined)
  static getSafeHitterID = (hitter: IHitter | undefined): string => {
    if (hitter?._id === ANONYMOUS_HITTER_ID) {
      return '';
    }

    return hitter?._id ?? '';
  };
}
